import { plausibleZeit } from './gpx'
import { asArray, isRecord, num, str } from './json'
import type { DiaryEntry, DiaryTrack, DiaryWeather, TrackFix, WeatherHour } from './types'

/**
 * Alles, was am Wettervorschlag zu entscheiden ist: wonach gefragt wird, was
 * daraus für ein Satz wird, und woran man mit dem Gespeicherten ist.
 *
 * Reine Funktionen und kein React, kein fetch: der Netzaufruf steht in
 * data/api.ts, die Bedienung in features/diary - hier stehen nur die Regeln,
 * und die sind ohne Browser prüfbar. Dieselbe Aufteilung wie bei lib/route.ts
 * und lib/geocode.ts.
 *
 * Die Regel, die alles bestimmt: **Wetter wird nur vorgeschlagen, wo eine
 * Aufzeichnung vorliegt.** Für Etappen ohne GPX-Track gibt es keinen
 * Vorschlag, auch keinen schwächeren aus Zielkoordinate und geratener
 * Ankunftszeit. Der abgeleitete Wert soll ein Tatsachenanker sein; eine Zahl
 * aus geratener Position und geratener Stunde ist keine Tatsache, sondern eine
 * plausible Zahl - und eine plausible Zahl, die aussieht wie eine Messung, ist
 * schlimmer als gar keine. Wer nicht aufgezeichnet hat, schreibt das Wetter
 * selbst hin; er war ja dabei.
 *
 * Was der abgeleitete Wert ausdrücklich nicht kann: „Regen bis Ljubljana,
 * danach Sonne.“ Das ist die Erzählung, und dafür ist das Tagebuch da. Der
 * Vorschlag liefert den Anker, nicht die Geschichte.
 */

/**
 * Wie viele Abtastpunkte höchstens gefragt werden.
 *
 * Zwölf, dieselbe Zahl wie MAX_TRACK_FIXES in lib/gpx.ts und MaxSamples im
 * Server. Der Dienst liefert stündlich; mehr Punkte wären eine Genauigkeit,
 * die es in der Antwort nicht gibt.
 */
export const MAX_WEATHER_SAMPLES = 12

/**
 * Die Namensnennung, die gilt, solange keine mit den Werten mitgekommen ist.
 *
 * Open-Meteo stellt seine Daten unter CC BY 4.0, und die verlangt die Nennung.
 * Der Regelfall ist, dass sie im Dokument steht (DiaryWeather.attribution) -
 * sie gehört zu den Werten und nicht zu einer Ansicht. Dieser Wert greift für
 * Bestände aus der Zeit vor dem Feld: lieber die Nennung eines Dienstes, der
 * es sehr wahrscheinlich war, als gar keine.
 */
export const WEATHER_ATTRIBUTION = 'Wetterdaten von Open-Meteo.com, CC BY 4.0'

/**
 * Der Abstand, ab dem die Auskunft zu einem Tag als endgültig gilt.
 *
 * Dieselben fünf Tage wie WeatherSettleAge im Server
 * (backend/internal/store/weather.go), und das ist kein Zufall, sondern
 * dieselbe Tatsache: das Archiv des Dienstes hinkt seiner Gegenwart um einige
 * Tage nach. Wer heute nach heute fragt, bekommt womöglich nichts; wer in einer
 * Woche nach demselben Tag fragt, bekommt Werte.
 *
 * Die Zahl steht hier ein zweites Mal, weil sie hier eine andere Frage
 * beantwortet: der Server entscheidet damit, wie lange er eine Antwort
 * aufbewahrt, der Client, ob er die Frage überhaupt für beantwortet hält. Ein
 * Feld in der Antwort („endgültig: ja/nein“) wäre eine zweite Wahrheit über
 * etwas, das beide Seiten aus zwei Zeitstempeln selbst ausrechnen können.
 */
export const WEATHER_SETTLE_AGE_MS = 5 * 24 * 3_600_000

/**
 * Das Alter, ab dem eine noch nicht gefestigte Auskunft erneut geholt wird.
 *
 * Ein Tag, dieselbe Spanne wie WeatherRecheckAge im Server. Kürzer wäre
 * sinnlos: der Server beantwortet eine Frage innerhalb dieses Tages aus seinem
 * Zwischenspeicher, es käme dieselbe leere Antwort zurück und stünde danach mit
 * neuem Zeitstempel im Tagebuch - eine Änderung am Bestand, die durch den
 * Abgleich geht und nichts sagt.
 */
export const WEATHER_RECHECK_AGE_MS = 24 * 3_600_000

/** Zustand des Wettervorschlags einer Etappe. */
export type WeatherState =
  /** Keine brauchbare Aufzeichnung - hier gibt es nichts vorzuschlagen. */
  | 'no-track'
  /** Aufzeichnung da, Wetter noch nicht geholt. */
  | 'none'
  /** Wetter da, aber für einen anderen Zeitbereich als den der Aufzeichnung. */
  | 'stale'
  /**
   * Gefragt und nichts bekommen, aber der Tag war beim Abruf noch zu frisch.
   *
   * Der Regelfall auf der Reise: die GPX-Datei wird am selben Abend eingelesen,
   * und das Archiv hat für heute noch nichts. Das ist keine Auskunft „für
   * diesen Tag gibt es nichts“, sondern ein „noch nicht“, und es wird von
   * selbst nachgeholt.
   */
  | 'pending'
  /** Wetter da und passend zur Aufzeichnung. */
  | 'current'

/**
 * Ist diese Auskunft endgültig?
 *
 * Gefragt wird an denselben zwei Zeitstempeln, an denen der Server es
 * entscheidet: wann geholt wurde und bis wann gefahren wurde. Lagen dazwischen
 * mindestens WEATHER_SETTLE_AGE_MS, war das Archiv des Dienstes für diesen Tag
 * fertig, und was er da sagte - auch ein Nichts -, sagt er auch morgen noch.
 *
 * Warum an der Zeitspanne und nicht am Zustand des Servers: der Client kennt
 * beide Zeitstempel, sie liegen im Tagebucheintrag (DiaryWeather) und gehen
 * durch Abgleich und Exportdatei mit. Der Zustand des Zwischenspeichers dagegen
 * gehört dem Server, steht nicht in der Antwort und wäre in dem Moment falsch,
 * in dem ein anderes Gerät dieselbe Etappe schon geholt hat.
 *
 * endedAt ist das Ende der Fahrt und damit frühestens die letzte gefragte
 * Stunde - bei einer Fahrt über mehr als MAX_WEATHER_SAMPLES Stunden liegt der
 * letzte Abtastpunkt davor. Die Abweichung geht in die vorsichtige Richtung:
 * der Client hält die Auskunft dann eher etwas zu spät für endgültig als zu
 * früh.
 */
export function weatherSettled(wetter: DiaryWeather): boolean {
  return wetter.fetchedAt - wetter.endedAt >= WEATHER_SETTLE_AGE_MS
}

/**
 * Ist es Zeit, eine noch nicht gefestigte Auskunft erneut zu holen?
 *
 * Getrennt vom Zustand und mit Absicht: was man über einen Tag weiss, ändert
 * sich nicht dadurch, dass eine Stunde vergeht - was man fragen sollte, schon.
 * Die Anzeige hängt am Zustand, der Nachlauf an dieser Frage.
 */
export function weatherRecheckDue(wetter: DiaryWeather, jetzt = Date.now()): boolean {
  return jetzt - wetter.fetchedAt >= WEATHER_RECHECK_AGE_MS
}

/**
 * Die Punkte, mit denen für diese Aufzeichnung gefragt wird.
 *
 * Sie kommen aus dem Track und werden nicht gerechnet: es sind echte
 * Aufzeichnungspunkte mit ihrer echten Uhrzeit (TrackFix in types.ts). Eine
 * leere Liste heisst „hier ist nichts zu fragen“ - dann bleibt es dabei, und
 * es geht keine Anfrage hinaus.
 *
 * Geprüft wird auch hier noch einmal, obwohl der Track aus dem eigenen Bestand
 * kommt: ein Bestand kann aus einer Importdatei stammen, und was aus einer
 * Datei kommt, ist eine fremde Behauptung.
 *
 * Das gilt für die Uhrzeit genauso wie für den Ort. Ein Empfänger mit
 * übergelaufenem Wochenzähler datiert die Fahrt 1024 Wochen zurück - aus 2026
 * wird 2006 -, und das Archiv des Dienstes reicht bis 1940 und antwortet auf
 * die Frage nach 2006 mit echten Messwerten. Daraus würde ein Satz über einen
 * Tag, an dem niemand unterwegs war: genau die plausible Zahl, die diese Datei
 * vermeiden will. Der Import fängt das schon ab (trackFixes in lib/gpx.ts) und
 * sagt es dem Nutzer; hier steht die Prüfung für alles, was vorher in den
 * Bestand gekommen ist.
 *
 * @param jetzt Bezugspunkt für die Glaubwürdigkeit der Uhrzeiten.
 */
export function weatherSamples(track: DiaryTrack | undefined, jetzt = Date.now()): TrackFix[] {
  if (!track) return []
  const out: TrackFix[] = []
  for (const fix of track.fixes ?? []) {
    if (!Array.isArray(fix.at) || fix.at.length < 2) continue
    const [lat, lon] = fix.at
    if (!usableFixCoord(lat, lon)) continue
    if (!Number.isFinite(fix.t)) continue
    if (!plausibleZeit(fix.t, jetzt)) continue
    // Zeitlich aufsteigend, höchstens einer je Stunde - genau das erwartet der
    // Server, und was er ablehnen würde, geht hier gar nicht erst los.
    const vorher = out[out.length - 1]
    if (vorher && Math.floor(fix.t / 3_600_000) <= Math.floor(vorher.t / 3_600_000)) continue
    out.push({ at: [lat, lon], t: fix.t })
    if (out.length >= MAX_WEATHER_SAMPLES) break
  }
  return out
}

/**
 * Ist das eine Koordinate, die es auf der Erde gibt?
 *
 * Genau 0/0 fällt bewusst mit heraus - dieselbe Regel wie beim GPX-Leser und
 * bei der Adresssuche. Dort steht kein Camper, so sieht ein leeres Feld aus,
 * das jemand als Zahl ausgegeben hat.
 */
function usableFixCoord(lat: number, lon: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false
  return lat !== 0 || lon !== 0
}

/**
 * Sagt, woran man mit dem Wetter einer Etappe ist.
 *
 * Die Reihenfolge der Prüfungen ist die Aussage: erst gibt es überhaupt eine
 * Aufzeichnung, dann etwas Geholtes, dann passt es noch zu ihr. Genau wie
 * routeState in lib/route.ts.
 *
 * „Passt noch“ heisst: derselbe Zeitbereich. Kommt eine zweite GPX-Datei dazu,
 * ist der Tag ein anderer - und eine Auskunft über den halben Tag, die aussieht
 * wie eine über den ganzen, ist schlechter als keine.
 *
 * Die letzte Prüfung ist die jüngste: eine leere Antwort ist nur dann die
 * endgültige Auskunft „für diesen Tag gibt es nichts“, wenn beim Abruf schon
 * Abstand genug zum Tag selbst war. Sonst hat der Dienst den Tag noch gar nicht
 * fertig, und dann ist die Frage offen und nicht beantwortet. Werte, die
 * dastehen, gelten dagegen: sie sind eine Beobachtung, auch wenn der Dienst sie
 * später noch verfeinert - eine Zahl, die sich beim nächsten Öffnen der App um
 * ein Grad verschiebt und einen bereits übernommenen Satz wieder zum Vorschlag
 * macht, wäre die schlechtere Auskunft.
 */
export function weatherState(entry: DiaryEntry | undefined, jetzt = Date.now()): WeatherState {
  const track = entry?.track
  if (!track || track.startedAt === null || track.endedAt === null) return 'no-track'
  // Eine Aufzeichnung, deren Uhr nicht gestellt war, ist für diese Frage so gut
  // wie keine: es gibt nichts zu holen, weil niemand weiss, wann die Fahrt war.
  // Der Nutzer erfährt den Grund beim Import (zeitWarnung in lib/gpx.ts).
  if (weatherSamples(track, jetzt).length === 0) return 'no-track'

  const wetter = entry?.weatherFromTrack
  if (!wetter) return 'none'
  if (wetter.startedAt !== track.startedAt || wetter.endedAt !== track.endedAt) return 'stale'
  if (wetter.hours.length === 0 && !weatherSettled(wetter)) return 'pending'
  return 'current'
}

/**
 * Der Satz, der zu dieser Etappe vorgeschlagen wird - oder nichts.
 *
 * Nichts kommt zurück, wenn es keine passenden Werte gibt oder der Dienst für
 * diesen Tag nichts hatte. Das ist kein Fehler: es gibt Tage und Ecken, für die
 * das Archiv keine Auskunft hat, und dann bleibt das Feld leer, statt dass
 * etwas dasteht, was niemand gemessen hat.
 */
export function weatherSuggestion(entry: DiaryEntry | undefined): string | null {
  if (weatherState(entry) !== 'current') return null
  const satz = weatherSentence(entry?.weatherFromTrack?.hours ?? [])
  return satz === '' ? null : satz
}

/** Ein Vorschlag samt dem, was er ersetzen würde. */
export interface WeatherProposal {
  /** Der vorgeschlagene Satz. */
  text: string
  /** Was gerade im Tagebuch steht. Leer heisst: dort steht nichts. */
  current: string
  /** Steht der Vorschlag schon genau so im Tagebuch? */
  accepted: boolean
}

/**
 * Der Vorschlag zu einer Etappe, oder nichts.
 *
 * Die Funktion schlägt vor und ändert nichts - sie liefert beides
 * nebeneinander: was vorgeschlagen wird und was dasteht. Genau daran hängt die
 * Regel des Projekts: ein Vorschlag überschreibt niemals, was jemand
 * eingetragen hat, und ist als Vorschlag erkennbar, bis er angenommen wurde.
 * Angenommen ist er erst, wenn der Nutzer gedrückt hat - und dann steht er
 * wörtlich im Feld, was hier genau so geprüft wird.
 *
 * Dieselbe Aufteilung wie bei routeProposal in lib/route.ts: die Zahlen des
 * Dienstes und die der Planung stehen nebeneinander, und der Nutzer wählt.
 */
export function weatherProposal(entry: DiaryEntry | undefined): WeatherProposal | null {
  const text = weatherSuggestion(entry)
  if (text === null) return null
  const current = (entry?.weather ?? '').trim()
  return { text, current, accepted: current === text }
}

/**
 * Die Lage des Himmels, gröber als der Wettercode.
 *
 * Die WMO kennt achtundzwanzig Codes; ein Mensch, der abends das Wetter des
 * Tages aufschreibt, kennt acht. „Mässiger Regen“ und „starker Regen“ sind für
 * das Buch derselbe Regen - die Unterscheidung wäre ein Messprotokoll und kein
 * Satz.
 */
type Lage = 'klar' | 'heiter' | 'bedeckt' | 'nebel' | 'niesel' | 'regen' | 'schnee' | 'gewitter'

/**
 * Der Wettercode der WMO als Lage, oder nichts.
 *
 * Ein unbekannter Code wird ausdrücklich nicht geraten: er fällt aus der
 * Phasenrechnung heraus, statt als „bedeckt“ durchzugehen. Der Dienst kann
 * seine Liste erweitern, und ein neuer Code, den wir als Wolke deuten, wäre
 * genau die plausible Behauptung, die es hier nicht geben soll.
 */
function lageVon(code: number): Lage | null {
  switch (code) {
    case 0:
      return 'klar'
    case 1:
    case 2:
      return 'heiter'
    case 3:
      return 'bedeckt'
    case 45:
    case 48:
      return 'nebel'
    case 51:
    case 53:
    case 55:
    case 56:
    case 57:
      return 'niesel'
    case 61:
    case 63:
    case 65:
    case 66:
    case 67:
    case 80:
    case 81:
    case 82:
      return 'regen'
    case 71:
    case 73:
    case 75:
    case 77:
    case 85:
    case 86:
      return 'schnee'
    case 95:
    case 96:
    case 99:
      return 'gewitter'
    default:
      return null
  }
}

/**
 * Lagen, die auch als einzelne Stunde im Satz stehen bleiben.
 *
 * Schnee und Gewitter sind die Nachricht des Tages, auch wenn sie eine Stunde
 * gedauert haben - „unterwegs ein Gewitter“ ist genau das, was abends jemand
 * aufschreiben würde. Alles andere ist in einer einzelnen Stunde eine Episode
 * und keine Phase, siehe fasseZusammen.
 */
const BLEIBT_ALS_EPISODE: ReadonlySet<Lage> = new Set<Lage>(['schnee', 'gewitter'])

/** Wie eine Lage im Satz heisst, tags und nachts. */
const WORT: Record<Lage, string> = {
  // Nachts ist die Sonne kein Argument. Der Sonderfall steht in wortFuer.
  klar: 'sonnig',
  heiter: 'heiter',
  bedeckt: 'bedeckt',
  nebel: 'Nebel',
  niesel: 'Nieselregen',
  regen: 'Regen',
  schnee: 'Schnee',
  gewitter: 'Gewitter',
}

/**
 * Das Wort für eine Lage, zur Ortszeit ihres Beginns.
 *
 * Der einzige Sonderfall ist die klare Nacht: „sonnig“ um zwei Uhr früh wäre
 * genau die Sorte Satz, die diese Funktion vermeiden soll - richtig gemessen
 * und trotzdem falsch.
 */
function wortFuer(lage: Lage, ortsstunde: number): string {
  if (lage === 'klar' && (ortsstunde >= 21 || ortsstunde < 6)) return 'klar'
  return WORT[lage]
}

/** Wie viele Phasen ein Satz höchstens beschreibt. */
const MAX_PHASEN = 3

/**
 * Ab welcher Länge der Fahrt eine einzelne Stunde als Episode gilt.
 *
 * Bei einer Fahrt von vier Stunden und mehr ist eine einzelne Stunde ein
 * Viertel oder weniger und geht im Satz unter. Bei einer Fahrt von zwei Stunden
 * ist sie die Hälfte des Tages und gehört hinein.
 */
const EPISODEN_SCHWELLE = 4

/** Eine Phase: eine Folge von Stunden mit derselben Lage. */
interface Phase {
  lage: Lage
  /** Die Stunden dieser Phase, in zeitlicher Reihenfolge. */
  stunden: WeatherHour[]
}

/** Die Ortszeit-Stunde eines Wertes: dort, wo die Reisenden waren. */
function ortsstunde(h: WeatherHour): number {
  return new Date(h.at + h.offsetSeconds * 1000).getUTCHours()
}

/**
 * Zeitwörter je Tagesabschnitt.
 *
 * Zwei Formen, weil der Satz sie beide braucht: „vormittags Regen“ für die
 * erste Phase und „ab Mittag sonnig“ für jede folgende. Eine Uhrzeit statt
 * eines Tagesabschnitts wäre genauer und würde sich schlechter lesen - bis auf
 * einen Fall, siehe satzTeile.
 */
const ABSCHNITTE: { bis: number; adverb: string; ab: string }[] = [
  { bis: 4, adverb: 'nachts', ab: 'ab der Nacht' },
  { bis: 8, adverb: 'morgens', ab: 'ab dem Morgen' },
  { bis: 11, adverb: 'vormittags', ab: 'ab dem Vormittag' },
  { bis: 13, adverb: 'mittags', ab: 'ab Mittag' },
  { bis: 17, adverb: 'nachmittags', ab: 'ab dem Nachmittag' },
  { bis: 21, adverb: 'abends', ab: 'ab dem Abend' },
  { bis: 23, adverb: 'nachts', ab: 'ab der Nacht' },
]

function abschnittVon(ortsstunde: number) {
  return ABSCHNITTE.find((a) => ortsstunde <= a.bis) ?? ABSCHNITTE[ABSCHNITTE.length - 1]!
}

/**
 * Baut aus den Stundenwerten entlang der Fahrt einen Satz, wie ihn ein Mensch
 * schreiben würde.
 *
 * „vormittags Regen, ab Mittag sonnig, 31 Grad“ - und nicht „18 Grad,
 * bewölkt“. Im gedruckten Buch soll das wie eine Beobachtung aussehen und nicht
 * wie ein Messwert.
 *
 * Der Satz ist knapp gehalten, und zwar mit Absicht: je mehr dasteht, desto
 * mehr davon ist Auslegung. Drei Teile, mehr nicht - der Verlauf des Himmels,
 * die Temperatur, und der Wind nur dann, wenn er auffiel.
 *
 * Eine leere Zeichenkette heisst „dazu lässt sich nichts sagen“. Sie kommt bei
 * einer leeren Liste und bei Werten, die die Prüfung nicht überstanden haben -
 * lieber nichts als etwas Erfundenes.
 */
export function weatherSentence(hours: WeatherHour[]): string {
  const werte = [...hours].sort((a, b) => a.at - b.at)
  if (werte.length === 0) return ''

  const teile = [...satzTeile(werte)]

  const temperatur = temperaturTeil(werte)
  if (temperatur) teile.push(temperatur)

  const wind = windTeil(werte)
  if (wind) teile.push(wind)

  return teile.join(', ')
}

/** Die Phasen des Himmels als Satzteile, in Fahrreihenfolge. */
function satzTeile(werte: WeatherHour[]): string[] {
  const phasen = fasseZusammen(phasenVon(werte), werte.length)
  if (phasen.length === 0) return []

  // Eine einzige Phase braucht kein Zeitwort: „sonnig, 24 Grad“ sagt alles,
  // „durchgehend sonnig“ wäre eine Behauptung über die Stunden dazwischen, für
  // die es keinen Wert gibt.
  const erste = phasen[0]!
  if (phasen.length === 1) return [wortFuer(erste.lage, ortsstunde(erste.stunden[0]!))]

  const teile: string[] = []
  let vorigerAbschnitt = ''
  for (const [i, phase] of phasen.entries()) {
    const stunde = ortsstunde(phase.stunden[0]!)
    const abschnitt = abschnittVon(stunde)
    const wort = wortFuer(phase.lage, stunde)

    if (i === 0) {
      teile.push(`${abschnitt.adverb} ${wort}`)
    } else if (abschnitt.adverb === vorigerAbschnitt) {
      // Zwei Phasen im selben Tagesabschnitt: „vormittags Regen, ab dem
      // Vormittag sonnig“ sagt nichts. Dann die Uhr, denn die ist genau und
      // liest sich immer noch wie ein Satz.
      teile.push(`ab ${String(stunde)} Uhr ${wort}`)
    } else {
      teile.push(`${abschnitt.ab} ${wort}`)
    }
    vorigerAbschnitt = abschnitt.adverb
  }
  return teile
}

/**
 * Zerlegt die Stunden in Folgen gleicher Lage.
 *
 * Stunden mit unbekanntem Wettercode fallen heraus und trennen dabei nicht:
 * eine Lücke in der Auskunft ist kein Wetterwechsel. Ihre Temperatur zählt
 * trotzdem mit, denn sie wurde gemessen - gerechnet wird sie in temperaturTeil
 * über alle Werte.
 */
function phasenVon(werte: WeatherHour[]): Phase[] {
  const phasen: Phase[] = []
  for (const h of werte) {
    const lage = lageVon(h.code)
    if (lage === null) continue
    const letzte = phasen[phasen.length - 1]
    if (letzte && letzte.lage === lage) letzte.stunden.push(h)
    else phasen.push({ lage, stunden: [h] })
  }
  return phasen
}

/**
 * Dampft die Phasen auf höchstens MAX_PHASEN ein.
 *
 * Zwei Regeln, in dieser Reihenfolge. Erst fallen einzelne Stunden weg, die
 * keine Phase sind, sondern eine Episode - ausser Schnee und Gewitter, die auch
 * in einer Stunde die Nachricht des Tages sind. Dann wird die jeweils kürzeste
 * Phase in ihre grössere Nachbarin geschoben, bis drei übrig sind.
 *
 * Geschoben wird in die grössere und nicht in die auffälligere Nachbarin: die
 * auffälligere zu wählen hiesse, den Tag im Zweifel dramatischer zu machen, als
 * er war. Bei gleich langen Nachbarinnen gewinnt die frühere - irgendeine Regel
 * muss es sein, und die frühere ist die, an die man sich abends zuerst
 * erinnert.
 */
function fasseZusammen(phasen: Phase[], gesamtStunden: number): Phase[] {
  let aktuell = phasen

  if (gesamtStunden >= EPISODEN_SCHWELLE) {
    for (;;) {
      if (aktuell.length <= 1) break
      const index = aktuell.findIndex(
        (p) => p.stunden.length === 1 && !BLEIBT_ALS_EPISODE.has(p.lage),
      )
      if (index < 0) break
      aktuell = verschmelze(aktuell, index)
    }
  }

  while (aktuell.length > MAX_PHASEN) {
    let kuerzeste = 0
    for (let i = 1; i < aktuell.length; i++) {
      if (aktuell[i]!.stunden.length < aktuell[kuerzeste]!.stunden.length) kuerzeste = i
    }
    aktuell = verschmelze(aktuell, kuerzeste)
  }
  return aktuell
}

/** Schiebt die Phase an dieser Stelle in ihre grössere Nachbarin. */
function verschmelze(phasen: Phase[], index: number): Phase[] {
  const links = phasen[index - 1]
  const rechts = phasen[index + 1]
  const nach = !links
    ? 'rechts'
    : !rechts
      ? 'links'
      : rechts.stunden.length > links.stunden.length
        ? 'rechts'
        : 'links'

  const rest = phasen.filter((_, i) => i !== index)
  const ziel = nach === 'links' ? rest[index - 1]! : rest[index]!
  ziel.stunden = [...ziel.stunden, ...phasen[index]!.stunden].sort((a, b) => a.at - b.at)

  // Die Nachbarinnen der weggefallenen Phase können jetzt dieselbe Lage haben.
  const zusammen: Phase[] = []
  for (const p of rest) {
    const letzte = zusammen[zusammen.length - 1]
    if (letzte && letzte.lage === p.lage) letzte.stunden = [...letzte.stunden, ...p.stunden]
    else zusammen.push({ lage: p.lage, stunden: [...p.stunden] })
  }
  return zusammen
}

/**
 * Spannweite, ab der die Temperatur als „von bis“ dasteht.
 *
 * Vier Grad. Darunter ist eine einzelne Zahl die ehrlichere Angabe - „18 bis 20
 * Grad“ tut so, als wäre der Unterschied eine Aussage. Darüber ist sie es: wer
 * morgens bei 8 Grad losfährt und nachmittags bei 28 ankommt, hat keinen
 * 28-Grad-Tag gehabt.
 */
const TEMPERATUR_SPANNE = 4

/** Die Temperatur des Tages, in ganzen Graden. */
function temperaturTeil(werte: WeatherHour[]): string {
  let min = Infinity
  let max = -Infinity
  for (const h of werte) {
    if (h.tempC < min) min = h.tempC
    if (h.tempC > max) max = h.tempC
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return ''

  const tief = Math.round(min)
  const hoch = Math.round(max)
  if (hoch - tief <= TEMPERATUR_SPANNE) return `${String(hoch)} Grad`
  return `${String(tief)} bis ${String(hoch)} Grad`
}

/** Ab hier fällt der Wind auf, und ab hier ist er das Thema des Tages. */
const WINDIG_KMH = 40
const STUERMISCH_KMH = 60

/**
 * Der Wind, aber nur wenn er auffiel.
 *
 * Meistens steht hier nichts, und das ist richtig so: eine Zahl zum Wind an
 * jedem Tag machte aus dem Satz ein Messprotokoll. Vierzig Stundenkilometer
 * sind die Grenze, ab der ein Wohnmobil auf der Autobahn merklich zieht.
 */
function windTeil(werte: WeatherHour[]): string {
  let max = 0
  for (const h of werte) {
    if (h.windKmh > max) max = h.windKmh
  }
  if (max >= STUERMISCH_KMH) return 'stürmisch'
  if (max >= WINDIG_KMH) return 'windig'
  return ''
}

// --- Was von aussen hereinkommt ---------------------------------------------

/**
 * Grenzen, ausserhalb derer ein Wert nicht mehr das Wetter dieser Erde
 * beschreibt.
 *
 * Dieselben wie im Server (internal/weather). Geprüft wird hier trotzdem noch
 * einmal, weil diese Seite es ist, die schreibt: was aus dem Netz kommt, ist
 * von hier aus eine fremde Behauptung, auch wenn es den eigenen Server passiert
 * hat. Derselbe Grundsatz wie beim Lesen einer GPX-Datei und der Adresssuche.
 */
const MIN_TEMP_C = -100
const MAX_TEMP_C = 70
const MAX_WIND_KMH = 500
const MAX_CODE = 99
/** Weiter als vierzehn Stunden ist keine Zeitzone von UTC entfernt. */
const MAX_OFFSET_SECONDS = 14 * 3600

/**
 * Macht aus der Antwort des Dienstes die Stundenwerte.
 *
 * Ein unbrauchbarer Wert fällt für sich heraus und verwirft nicht die ganze
 * Antwort: der Verlauf ist eine Aufzählung von Stunden, und eine Stunde weniger
 * behauptet nichts. Anders als bei der Geometrie einer Strecke, wo ein
 * übersprungener Punkt eine Abkürzung erfände, die es nicht gibt.
 *
 * @param raw das Feld hours der Antwort, ungeprüft.
 */
export function weatherHoursFrom(raw: unknown): WeatherHour[] {
  const out: WeatherHour[] = []
  for (const eintrag of asArray(raw)) {
    if (!isRecord(eintrag)) continue
    const at = zahl(eintrag.at)
    const offsetSeconds = zahl(eintrag.offsetSeconds)
    const tempC = zahl(eintrag.tempC)
    const code = zahl(eintrag.code)
    const windKmh = zahl(eintrag.windKmh)
    if (!Number.isFinite(at) || at <= 0) continue
    if (!Number.isFinite(offsetSeconds) || Math.abs(offsetSeconds) > MAX_OFFSET_SECONDS) continue
    if (!Number.isFinite(tempC) || tempC < MIN_TEMP_C || tempC > MAX_TEMP_C) continue
    if (!Number.isInteger(code) || code < 0 || code > MAX_CODE) continue
    if (!Number.isFinite(windKmh) || windKmh < 0 || windKmh > MAX_WIND_KMH) continue
    out.push({ at, offsetSeconds, tempC, code, windKmh })
  }
  return out.sort((a, b) => a.at - b.at)
}

/**
 * Eine Zahl, die auch wirklich als Zahl geliefert wurde - sonst NaN.
 *
 * Ausdrücklich nicht num() aus lib/json.ts: dessen Umweg über Number() macht
 * aus einer leeren Zeichenkette eine 0, und eine 0 ist hier keine fehlende
 * Angabe, sondern ein Messwert - der Gefrierpunkt.
 */
function zahl(v: unknown): number {
  return typeof v === 'number' ? v : NaN
}

/**
 * Das gespeicherte Wetter eines Tagebucheintrags aus einer Importdatei.
 *
 * Fehlt es, ist es keines: Dateien aus der Zeit vor dem Feld kennen es nicht,
 * und die allermeisten Einträge haben keines. Ohne Zeitbereich gibt es keines -
 * ohne ihn liesse sich nicht mehr sagen, zu welcher Fahrt die Werte gehören,
 * und eine Auskunft über einen unbekannten Zeitraum ist keine.
 *
 * Eine leere Stundenliste bleibt ausdrücklich erhalten: „für diesen Tag hat der
 * Dienst nichts“ ist eine Auskunft, und sie soll nach einem Import nicht wieder
 * zu einer offenen Frage werden. Ob sie die endgültige Auskunft ist, entscheidet
 * danach weatherState an fetchedAt und endedAt - ein Import ändert daran nichts.
 */
export function weatherFromRaw(v: unknown): DiaryWeather | undefined {
  if (!isRecord(v)) return undefined
  const startedAt = num(v.startedAt, NaN)
  const endedAt = num(v.endedAt, NaN)
  if (!Number.isFinite(startedAt) || !Number.isFinite(endedAt) || endedAt < startedAt) {
    return undefined
  }
  return {
    hours: weatherHoursFrom(v.hours),
    fetchedAt: num(v.fetchedAt, 0),
    source: str(v.source),
    // Leer heisst „unbekannt“, und angezeigt wird dann die Vorgabe: eine
    // Nennung, die verlorengeht, ist ein Lizenzverstoss, kein Schönheitsfehler.
    attribution: str(v.attribution) || WEATHER_ATTRIBUTION,
    startedAt,
    endedAt,
  }
}
